import { useState } from 'react'
import type { ReactNode } from 'react'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ConfirmDialog, Dialog } from '@/components/Dialog'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { Checkbox, TextAreaField, TextField } from '@/components/Form'
import { Skeleton, SkeletonRows } from '@/components/Skeleton'
import {
  useAdminProfiles,
  useDeleteProfile,
  useJellyfinLibraries,
  useSaveProfile,
} from '@/hooks/useAdmin'
import type { Profile, ProfileInput } from '@/lib/types'

interface Draft {
  name: string
  description: string
  isDefault: boolean
  accountExpiryDays: string
  allLibraries: boolean
  libraryIds: string[]
  seerrMovieQuotaLimit: string
  seerrMovieQuotaDays: string
  seerrTvQuotaLimit: string
  seerrTvQuotaDays: string
}

const numberToInput = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value)

const inputToNumber = (value: string): number | null => (value.trim() ? Number(value) : null)

function toDraft(profile: Profile | null): Draft {
  return {
    name: profile?.name ?? '',
    description: profile?.description ?? '',
    isDefault: profile?.isDefault ?? false,
    accountExpiryDays: numberToInput(profile?.accountExpiryDays),
    allLibraries: !profile || profile.libraryIds === null,
    libraryIds: profile?.libraryIds ?? [],
    seerrMovieQuotaLimit: numberToInput(profile?.seerrMovieQuotaLimit),
    seerrMovieQuotaDays: numberToInput(profile?.seerrMovieQuotaDays),
    seerrTvQuotaLimit: numberToInput(profile?.seerrTvQuotaLimit),
    seerrTvQuotaDays: numberToInput(profile?.seerrTvQuotaDays),
  }
}

const librariesLabel = (profile: Profile): string =>
  profile.libraryIds === null ? 'All' : String(profile.libraryIds.length)

const expiryLabel = (profile: Profile): string =>
  profile.accountExpiryDays ? `${profile.accountExpiryDays} days` : 'Never'

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="signage text-[9px] text-faint">{label}</dt>
      <dd className="mt-0.5 truncate font-mono text-xs text-ink">{value}</dd>
    </div>
  )
}

function ProfileEditor({
  profile,
  open,
  onClose,
}: {
  profile: Profile | null
  open: boolean
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(profile))
  const save = useSaveProfile()
  const libraries = useJellyfinLibraries(open)

  // Remount per target profile keeps this local state honest (see `key` below).
  const libraryItems = libraries.data?.items ?? []

  function submit() {
    const input: ProfileInput = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      isDefault: draft.isDefault,
      libraryIds: draft.allLibraries ? null : draft.libraryIds,
      accountExpiryDays: inputToNumber(draft.accountExpiryDays),
      seerrMovieQuotaLimit: inputToNumber(draft.seerrMovieQuotaLimit),
      seerrMovieQuotaDays: inputToNumber(draft.seerrMovieQuotaDays),
      seerrTvQuotaLimit: inputToNumber(draft.seerrTvQuotaLimit),
      seerrTvQuotaDays: inputToNumber(draft.seerrTvQuotaDays),
    }
    save.mutate({ id: profile?.id, input }, { onSuccess: onClose })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={profile ? `Edit “${profile.name}”` : 'New profile'}
      description="Profiles decide what a new account can see and how long it lasts."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!draft.name.trim()}
            onClick={submit}
          >
            Save profile
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Name"
          required
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
        <TextAreaField
          label="Description"
          rows={2}
          value={draft.description}
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
        />
        <TextField
          label="Account expiry (days)"
          type="number"
          min={1}
          placeholder="Never expires"
          value={draft.accountExpiryDays}
          onChange={(event) => setDraft({ ...draft, accountExpiryDays: event.target.value })}
        />

        <fieldset className="space-y-2 rounded border border-line p-3">
          <legend className="signage px-1 text-[9px] text-faint">Jellyfin libraries</legend>
          <Checkbox
            label="All libraries"
            checked={draft.allLibraries}
            onChange={(next) => setDraft({ ...draft, allLibraries: next })}
          />
          {!draft.allLibraries ? (
            libraries.isPending ? (
              <div className="space-y-2 pt-1">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-32" />
              </div>
            ) : libraries.isError ? (
              <ErrorNotice error={libraries.error} onRetry={() => void libraries.refetch()} />
            ) : libraryItems.length === 0 ? (
              <p className="text-sm text-muted">Jellyfin reported no libraries.</p>
            ) : (
              <div className="grid gap-2 pt-1 sm:grid-cols-2">
                {libraryItems.map((library) => (
                  <Checkbox
                    key={library.id}
                    label={library.name}
                    checked={draft.libraryIds.includes(library.id)}
                    onChange={(next) =>
                      setDraft({
                        ...draft,
                        libraryIds: next
                          ? [...draft.libraryIds, library.id]
                          : draft.libraryIds.filter((id) => id !== library.id),
                      })
                    }
                  />
                ))}
              </div>
            )
          ) : null}
        </fieldset>

        <fieldset className="grid gap-3 rounded border border-line p-3 sm:grid-cols-2">
          <legend className="signage px-1 text-[9px] text-faint">Request quotas</legend>
          <TextField
            label="Movies per period"
            type="number"
            min={0}
            placeholder="Unlimited"
            value={draft.seerrMovieQuotaLimit}
            onChange={(event) => setDraft({ ...draft, seerrMovieQuotaLimit: event.target.value })}
          />
          <TextField
            label="Movie period (days)"
            type="number"
            min={1}
            placeholder="—"
            value={draft.seerrMovieQuotaDays}
            onChange={(event) => setDraft({ ...draft, seerrMovieQuotaDays: event.target.value })}
          />
          <TextField
            label="Series per period"
            type="number"
            min={0}
            placeholder="Unlimited"
            value={draft.seerrTvQuotaLimit}
            onChange={(event) => setDraft({ ...draft, seerrTvQuotaLimit: event.target.value })}
          />
          <TextField
            label="Series period (days)"
            type="number"
            min={1}
            placeholder="—"
            value={draft.seerrTvQuotaDays}
            onChange={(event) => setDraft({ ...draft, seerrTvQuotaDays: event.target.value })}
          />
        </fieldset>

        <Checkbox
          label="Use this profile by default for new invites"
          checked={draft.isDefault}
          onChange={(next) => setDraft({ ...draft, isDefault: next })}
        />
      </div>
    </Dialog>
  )
}

const TH = 'signage px-4 py-2.5 text-[9px] font-semibold text-faint'
const TD = 'px-4 py-3 align-middle'

export function AdminProfilesPage() {
  const profiles = useAdminProfiles()
  const remove = useDeleteProfile()
  const [editing, setEditing] = useState<{ profile: Profile | null } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Profile | null>(null)

  const items = profiles.data?.items ?? []

  return (
    <Card>
      <CardHeader
        title={`Profiles${items.length ? ` (${items.length})` : ''}`}
        description="Applied to Jellyfin when someone signs up, or when you re-apply."
        action={
          <Button variant="primary" size="sm" onClick={() => setEditing({ profile: null })}>
            New profile
          </Button>
        }
      />
      <CardBody flush>
        {profiles.isPending ? (
          <div className="p-4">
            <SkeletonRows rows={3} />
          </div>
        ) : profiles.isError ? (
          <div className="p-4">
            <ErrorNotice error={profiles.error} onRetry={() => void profiles.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="No profiles yet"
              description="A profile bundles library access, expiry and request quotas so every invite lands the same way."
              action={
                <Button variant="primary" size="sm" onClick={() => setEditing({ profile: null })}>
                  Create the first profile
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {/* Phones: stacked record, no sideways scrolling. */}
            <div className="sm:hidden">
              {items.map((profile) => (
                <div key={profile.id} className="board-row px-4 py-3.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="signage-tight min-w-0 truncate text-sm text-ink">
                      {profile.name}
                    </p>
                    {profile.isDefault ? <Badge tone="accent">Default</Badge> : null}
                  </div>

                  {profile.description ? (
                    <p className="mt-1.5 text-sm text-muted">{profile.description}</p>
                  ) : null}

                  <dl className="mt-3 grid grid-cols-3 gap-x-3 gap-y-2.5">
                    <Field label="Libraries" value={librariesLabel(profile)} />
                    <Field label="Expiry" value={expiryLabel(profile)} />
                    <Field
                      label="Users"
                      value={typeof profile.userCount === 'number' ? profile.userCount : '—'}
                    />
                  </dl>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button size="sm" onClick={() => setEditing({ profile })}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPendingDelete(profile)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden sm:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className={TH}>
                      Profile
                    </th>
                    <th scope="col" className={TH}>
                      Libraries
                    </th>
                    <th scope="col" className={TH}>
                      Expiry
                    </th>
                    <th scope="col" className={TH}>
                      Users
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((profile) => (
                    <tr key={profile.id} className="border-b border-line last:border-b-0">
                      <td className={TD}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="signage-tight text-sm text-ink">{profile.name}</span>
                          {profile.isDefault ? <Badge tone="accent">Default</Badge> : null}
                        </div>
                        {profile.description ? (
                          <p className="mt-0.5 text-xs text-muted">{profile.description}</p>
                        ) : null}
                      </td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {librariesLabel(profile)}
                      </td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {expiryLabel(profile)}
                      </td>
                      <td className={`${TD} font-mono text-xs text-muted`}>
                        {typeof profile.userCount === 'number' ? profile.userCount : '—'}
                      </td>
                      <td className={TD}>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => setEditing({ profile })}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPendingDelete(profile)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardBody>

      {editing ? (
        <ProfileEditor
          key={editing.profile?.id ?? 'new'}
          open
          profile={editing.profile}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        loading={remove.isPending}
        destructive
        confirmLabel="Delete profile"
        title={`Delete “${pendingDelete?.name ?? ''}”?`}
        description="Users keep their current Jellyfin access; they simply stop being linked to this profile."
        onConfirm={() => {
          if (!pendingDelete) return
          remove.mutate(
            { id: pendingDelete.id, name: pendingDelete.name },
            { onSettled: () => setPendingDelete(null) },
          )
        }}
      />
    </Card>
  )
}
